import { useEffect, useRef, useState } from 'react'
import { Video, VideoOff, Mic, MicOff, Volume2 } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'
import { requestVideoStream } from '../../services/cameraPermission'

// OSPROD-103 (pre-join): a "check your devices" panel for the invite-link
// landing (GuestJoinPage) shown for VOICE channels BEFORE the user joins —
// camera preview + mirror toggle, a mic level meter, and camera/mic/speaker
// pickers + a speaker test tone, Discord/Zoom-style.
//
// Safety: there is NO room / denoiser yet on this page, so the preview and
// meter use one-shot getUserMedia that is FULLY torn down on toggle-off,
// device change, and unmount. GuestJoinPage unmounts the moment the user
// joins (navigate → channel), so these test captures always stop before the
// call's mediaManager mic capture starts — the voice-runtime invariant
// (no getUserMedia({audio}) competing with the denoiser cascade) is not
// touched. Device choices persist to voiceStore so they carry into the call.

interface Dev {
  deviceId: string
  label: string
}

export default function PreJoinDeviceCheck() {
  const videoInputDeviceId = useVoiceStore((s) => s.videoInputDeviceId)
  const setVideoInputDevice = useVoiceStore((s) => s.setVideoInputDevice)
  const audioInputDeviceId = useVoiceStore((s) => s.audioInputDeviceId)
  const setAudioInputDevice = useVoiceStore((s) => s.setAudioInputDevice)
  const audioOutputDeviceId = useVoiceStore((s) => s.audioOutputDeviceId)
  const setAudioOutputDevice = useVoiceStore((s) => s.setAudioOutputDevice)
  const selfMirror = useVoiceStore((s) => s.selfMirror)
  const setSelfMirror = useVoiceStore((s) => s.setSelfMirror)

  const [cams, setCams] = useState<Dev[]>([])
  const [mics, setMics] = useState<Dev[]>([])
  const [spks, setSpks] = useState<Dev[]>([])
  const [camOn, setCamOn] = useState(false)
  const [micOn, setMicOn] = useState(false)
  const [micLevel, setMicLevel] = useState(0)
  const [camErr, setCamErr] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const videoStreamRef = useRef<MediaStream | null>(null)
  const sinkSupported = 'setSinkId' in HTMLMediaElement.prototype

  const enumerate = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      setCams(all.filter((d) => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` })))
      setMics(all.filter((d) => d.kind === 'audioinput' && d.deviceId !== 'communications')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` })))
      setSpks(all.filter((d) => d.kind === 'audiooutput' && d.deviceId !== 'communications')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Speaker ${i + 1}` })))
    } catch {
      /* enumerate unavailable */
    }
  }

  useEffect(() => {
    enumerate()
    navigator.mediaDevices.addEventListener('devicechange', enumerate)
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate)
  }, [])

  // Camera preview — only while "Test camera" is on. Video-only, one-shot.
  useEffect(() => {
    let cancelled = false
    const stop = () => {
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } })
        videoStreamRef.current = null
      }
      if (videoRef.current) videoRef.current.srcObject = null
    }
    if (!camOn) { stop(); return }
    ;(async () => {
      stop()
      try {
        // OSPROD-145: cameraPermission circuit breaker.
        const stream = await requestVideoStream({
          video: videoInputDeviceId ? { deviceId: { exact: videoInputDeviceId } } : true,
        })
        if (cancelled) { stream.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } }); return }
        videoStreamRef.current = stream
        setCamErr(null)
        enumerate() // labels are available now that permission is granted
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}) }
      } catch {
        if (!cancelled) setCamErr('Camera unavailable or blocked.')
      }
    })()
    return () => { cancelled = true; stop() }
  }, [camOn, videoInputDeviceId])

  // Mic level meter — only while "Test mic" is on. One-shot audio capture,
  // fully released on cleanup.
  useEffect(() => {
    if (!micOn) { setMicLevel(0); return }
    let raf = 0
    let cancelled = false
    let ctx: AudioContext | null = null
    let src: MediaStreamAudioSourceNode | null = null
    let stream: MediaStream | null = null
    ;(async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: audioInputDeviceId ? { deviceId: { exact: audioInputDeviceId } } : true,
        })
        if (cancelled) { stream.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } }); return }
        enumerate()
        ctx = new AudioContext()
        src = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        src.connect(analyser)
        const data = new Uint8Array(analyser.frequencyBinCount)
        const loop = () => {
          if (cancelled) return
          analyser.getByteTimeDomainData(data)
          let peak = 0
          for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i] - 128); if (v > peak) peak = v }
          setMicLevel(Math.min(1, peak / 110))
          raf = requestAnimationFrame(loop)
        }
        loop()
      } catch {
        /* mic blocked */
      }
    })()
    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      try { src?.disconnect() } catch { /* noop */ }
      try { ctx?.close() } catch { /* noop */ }
      if (stream) stream.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } })
      setMicLevel(0)
    }
  }, [micOn, audioInputDeviceId])

  const testSpeaker = async () => {
    try {
      const ctx = new AudioContext()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      gain.gain.value = 0.08
      osc.frequency.value = 440
      const dest = ctx.createMediaStreamDestination()
      osc.connect(gain).connect(dest)
      const el = new Audio()
      el.srcObject = dest.stream
      if (sinkSupported && audioOutputDeviceId && audioOutputDeviceId !== 'default') {
        try { await (el as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(audioOutputDeviceId) } catch { /* default */ }
      }
      await el.play().catch(() => {})
      osc.start()
      setTimeout(() => {
        try { osc.stop() } catch { /* noop */ }
        try { el.pause() } catch { /* noop */ }
        try { ctx.close() } catch { /* noop */ }
      }, 500)
    } catch {
      /* AudioContext blocked */
    }
  }

  const selectCls = 'w-full bg-card text-foreground text-xs rounded px-2 py-1.5 border border-border focus:border-primary outline-none'

  return (
    <div className="mb-5 rounded-lg bg-muted/40 ring-1 ring-border p-3 text-left">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase mb-2">Check your devices</div>

      {/* Camera preview */}
      <div className="relative w-full aspect-video bg-background rounded-lg overflow-hidden ring-1 ring-border mb-2 flex items-center justify-center">
        {camOn ? (
          <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover ${selfMirror ? '-scale-x-100' : ''}`} />
        ) : (
          <span className="text-xs text-muted-foreground">Camera preview off</span>
        )}
        {camErr && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground px-4 text-center bg-background/80">{camErr}</div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => setCamOn((v) => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded bg-card text-foreground border border-border hover:bg-accent transition"
        >
          {camOn ? <VideoOff size={14} /> : <Video size={14} />}
          {camOn ? 'Stop camera' : 'Test camera'}
        </button>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer ml-auto">
          <input type="checkbox" checked={selfMirror} onChange={(e) => setSelfMirror(e.target.checked)} className="w-3.5 h-3.5 accent-primary" />
          Mirror
        </label>
      </div>
      {cams.length > 0 && (
        <select className={selectCls + ' mb-3'} value={videoInputDeviceId ?? ''} onChange={(e) => setVideoInputDevice(e.target.value)}>
          {!videoInputDeviceId && <option value="">Select camera…</option>}
          {cams.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
        </select>
      )}

      {/* Microphone */}
      <div className="flex items-center gap-2 mb-1.5">
        <button
          onClick={() => setMicOn((v) => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded bg-card text-foreground border border-border hover:bg-accent transition shrink-0"
        >
          {micOn ? <MicOff size={14} /> : <Mic size={14} />}
          {micOn ? 'Stop mic' : 'Test mic'}
        </button>
        <div className="flex-1 h-2.5 bg-background rounded-full overflow-hidden ring-1 ring-border">
          <div className="h-full bg-green-500 transition-[width] duration-75" style={{ width: `${Math.round(micLevel * 100)}%` }} />
        </div>
      </div>
      {mics.length > 0 && (
        <select className={selectCls + ' mb-3'} value={audioInputDeviceId ?? ''} onChange={(e) => setAudioInputDevice(e.target.value)}>
          {!audioInputDeviceId && <option value="">Select microphone…</option>}
          {mics.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
        </select>
      )}

      {/* Speaker */}
      <div className="flex items-center gap-2">
        <button
          onClick={testSpeaker}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded bg-card text-foreground border border-border hover:bg-accent transition shrink-0"
        >
          <Volume2 size={14} />
          Test speaker
        </button>
        {sinkSupported && spks.length > 0 && (
          <select className={selectCls} value={audioOutputDeviceId ?? ''} onChange={(e) => setAudioOutputDevice(e.target.value)}>
            {!audioOutputDeviceId && <option value="">Default speaker</option>}
            {spks.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
          </select>
        )}
      </div>
    </div>
  )
}
